import type {
  Breadcrumb,
  BrowserOptions,
  Event,
  ReplayFrameEvent,
} from "@sentry/react"

/**
 * Credentials ride in this app's URLs: an API token or an OAuth code in a query
 * string, a password-reset or invite nonce in a page URL, a presigned upload
 * signature, the public review token in a path. The URLs in breadcrumbs, error
 * and transaction events and spans go through `scrubUrl` at the hooks in
 * `sentryScrubOptions`; the Redux enhancer's state goes through
 * `sentryStateTransformer`. Session Replay passes none of these hooks:
 * `scrubRecordingEvent` and `replayEventScrubIntegration` cover its
 * recording and its replay_event. The rrweb Meta event at each recording
 * checkout keeps the raw page URL, as no Replay hook gets it.
 */

/** The marker Sentry's server-side scrubbing writes in place of a value. */
export const FILTERED = "[Filtered]"

/** Query and fragment parameters whose value is a credential, by name. */
const SECRET_PARAM =
  /token|secret|password|passwd|api[-_]?key|nonce|signature|^(auth_)?code$/i
/** A `name=value` pair after `?`, `&` or `#`. */
const URL_PARAM = /([?&#])([^=&#?\s]+)=[^&#\s]*/g
/** The review token: page `/review/:token`, API `/public/ad-review/:token`. */
const REVIEW_TOKEN = /(\/(?:public\/ad-)?review\/)[^/?#\s]+/g

/** Also takes a string that holds a URL, such as `GET /path?token=…`. */
export function scrubUrl(url: string): string {
  return url
    .replace(URL_PARAM, (param, separator: string, name: string) =>
      SECRET_PARAM.test(name) ? `${separator}${name}=${FILTERED}` : param,
    )
    .replace(REVIEW_TOKEN, `$1${FILTERED}`)
}

const BREADCRUMB_URL_FIELDS = new Map([
  ["fetch", ["url"]],
  ["xhr", ["url"]],
  ["navigation", ["from", "to"]],
  // Replay's own, which only `beforeAddRecordingEvent` gets.
  ["ui.slowClickDetected", ["url"]],
  ["ui.multiClick", ["url"]],
])

/**
 * Returns a copy: a failed fetch's breadcrumb data is the fetch
 * instrumentation's own object. Replay builds its network entries from this
 * copy too.
 */
export function scrubBreadcrumb<B extends Breadcrumb>(breadcrumb: B): B {
  const fields = BREADCRUMB_URL_FIELDS.get(breadcrumb.category ?? "")
  if (!fields || !breadcrumb.data) return breadcrumb

  const data = { ...breadcrumb.data }
  for (const field of fields) {
    if (typeof data[field] === "string") data[field] = scrubUrl(data[field])
  }
  return { ...breadcrumb, data }
}

/**
 * For error, transaction and replay events. The route name is the raw
 * pathname, as no route is registered with Sentry. The hooks get the SDK's own
 * copy of the event, so the fields change in place.
 */
export function scrubEvent<E extends Event>(event: E): E {
  const { request } = event
  if (request?.url) request.url = scrubUrl(request.url)
  if (request?.headers?.Referer) {
    request.headers.Referer = scrubUrl(request.headers.Referer)
  }
  if (event.transaction) event.transaction = scrubUrl(event.transaction)
  return event
}

type SpanJSON = Parameters<NonNullable<BrowserOptions["beforeSendSpan"]>>[0]

/** A copy whose string values went through `scrubUrl`. */
function scrubStringValues(data: object): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      typeof value === "string" ? scrubUrl(value) : value,
    ]),
  )
}

/**
 * A fetch or XHR span keeps the request URL in its description and its `url`
 * and `http.url` attributes; a standalone web-vital span keeps the route name
 * in `transaction`.
 */
export function scrubSpan(span: SpanJSON): SpanJSON {
  return {
    ...span,
    ...(span.description && { description: scrubUrl(span.description) }),
    ...(span.data && { data: scrubStringValues(span.data) }),
  }
}

/** For `Sentry.init`. */
export const sentryScrubOptions = {
  beforeBreadcrumb: scrubBreadcrumb,
  beforeSend: scrubEvent,
  beforeSendTransaction: scrubEvent,
  beforeSendSpan: scrubSpan,
} satisfies BrowserOptions

/**
 * For Replay's `beforeAddRecordingEvent`, which gets the recording's
 * breadcrumbs and performance spans. A span keeps a URL in its description:
 * the page of a page load or a history change, or a loaded resource such as a
 * presigned image. A history change also keeps the page before it in
 * `data.previous`.
 */
export function scrubRecordingEvent(event: ReplayFrameEvent): ReplayFrameEvent {
  const { data } = event
  switch (data.tag) {
    case "breadcrumb":
      return {
        ...event,
        data: { ...data, payload: scrubBreadcrumb(data.payload) },
      }
    case "performanceSpan": {
      const { payload } = data
      return {
        ...event,
        data: {
          ...data,
          payload: {
            ...payload,
            description: scrubUrl(payload.description),
            ...(payload.data && { data: scrubStringValues(payload.data) }),
          },
        },
      }
    }
    default:
      return event
  }
}

/**
 * For `Sentry.init`'s integrations. Replay sends its replay_event through the
 * event processors, but through no before-send hook. The event lists the pages
 * of the replay in `urls`, and HttpContext adds the page URL and the referrer.
 */
export function replayEventScrubIntegration() {
  return {
    name: "ReplayEventScrub",
    processEvent: (event: Event & { urls?: string[] }) => {
      if (event.type !== "replay_event") return event
      if (event.urls) event.urls = event.urls.map(scrubUrl)
      return scrubEvent(event)
    },
  }
}

/** An RTK Query slice names its own reducerPath in its `config`. */
function isRtkQueryState(key: string, slice: unknown): boolean {
  return (
    (slice as { config?: { reducerPath?: unknown } } | null)?.config
      ?.reducerPath === key
  )
}

/**
 * Sentry's Redux enhancer puts the latest state on every error event: as the
 * `state` context, `normalizeDepth` deep, and whole as `redux_state.json`. The
 * RTK Query slices hold every cached request argument and response body (a
 * reset nonce, a freshly minted API key, presigned URLs), so they are left out
 * and the app's own slices stay. This runs on every dispatch, so it only reads
 * the top level.
 */
export function sentryStateTransformer<S extends object>(state: S): Partial<S> {
  return Object.fromEntries(
    Object.entries(state).filter(
      ([key, slice]) => !isRtkQueryState(key, slice),
    ),
  ) as Partial<S>
}
